import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { X, Loader2, ChevronRight, ChevronLeft, Check, AlertTriangle, Upload, ArrowUpRight, ArrowDownLeft } from "lucide-react";

const SCOPE_TYPES = [
  { id: "clubs",   label: "Specific clubs",   blurb: "List the exact clubs allowed in the mandate." },
  { id: "leagues", label: "Specific leagues", blurb: "Permission to approach all clubs in named leagues / countries." },
  { id: "global",  label: "Global",           blurb: "Permission everywhere — broadest scope." },
  { id: "custom",  label: "Custom",           blurb: "Free-text scope; useful for unusual arrangements." },
];

export default function MandateWizard({ direction, onClose, onCreated }) {
  const [step, setStep] = useState(1); // 1: player · 2: partner · 3: scope · 4: dates+commission · 5: confirm + optional upload
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [pickedClient, setPickedClient] = useState(null);

  const [form, setForm] = useState({
    partner_agent_name: "",
    partner_agency_name: "",
    partner_agent_email: "",
    partner_agent_phone: "",
    scope_type: "clubs",
    scope_description: "",
    scope_clubs: [],
    scope_leagues: [],
    start_date: new Date().toISOString().slice(0, 10),
    expiry_date: "",
    reminder_days_before: 30,
    commission_split_pct: "",
    notes: "",
  });
  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const [file, setFile] = useState(null);

  useEffect(() => {
    api.get("/clients").then((r) => {
      const arr = r.data || [];
      // Outgoing → only Tier 1 clients are eligible
      // Incoming → only Tier 3 mandated players are eligible
      const allowed = direction === "outgoing"
        ? arr.filter((c) => (c.tier || "client") === "client")
        : arr.filter((c) => c.tier === "mandated");
      setClients(allowed);
    });
  }, [direction]);

  const filteredClients = useMemo(() => {
    if (!search) return clients;
    const s = search.toLowerCase();
    return clients.filter((c) =>
      c.full_name.toLowerCase().includes(s) ||
      c.current_club?.toLowerCase().includes(s) ||
      c.position?.toLowerCase().includes(s),
    );
  }, [clients, search]);

  const canAdvance = () => {
    if (step === 1) return !!pickedClient;
    if (step === 2) return !!form.partner_agent_name.trim();
    if (step === 3) {
      if (form.scope_type === "clubs") return form.scope_clubs.length > 0;
      if (form.scope_type === "leagues") return form.scope_leagues.length > 0;
      if (form.scope_type === "custom") return !!form.scope_description.trim();
      return true;
    }
    if (step === 4) return !!form.start_date && !!form.expiry_date;
    return true;
  };

  const submit = async () => {
    setCreating(true);
    setError(null);
    try {
      const payload = {
        direction,
        client_id: pickedClient.id,
        partner_agent_name: form.partner_agent_name.trim(),
        partner_agency_name: form.partner_agency_name.trim() || null,
        partner_agent_email: form.partner_agent_email.trim() || null,
        partner_agent_phone: form.partner_agent_phone.trim() || null,
        scope_type: form.scope_type,
        scope_description: form.scope_description.trim() || null,
        scope_clubs: form.scope_clubs,
        scope_leagues: form.scope_leagues,
        start_date: form.start_date,
        expiry_date: form.expiry_date,
        reminder_days_before: parseInt(form.reminder_days_before, 10) || 30,
        commission_split_pct: form.commission_split_pct === "" ? null : parseFloat(form.commission_split_pct),
        notes: form.notes.trim() || null,
      };
      const { data } = await api.post("/mandates", payload);
      // Optional doc upload
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/mandates/${data.id}/document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      onCreated();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not create mandate");
    } finally {
      setCreating(false);
    }
  };

  const Title = direction === "outgoing" ? "New outgoing mandate" : "New incoming mandate";
  const DirIcon = direction === "outgoing" ? ArrowUpRight : ArrowDownLeft;
  const dirTone = direction === "outgoing" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700";

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="mandate-wizard">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3 shrink-0">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${dirTone}`}>
            <DirIcon size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Step {step} of 5</div>
            <div className="font-display text-lg font-bold tracking-tight">{Title}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="wizard-close-btn">
            <X size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 pm-scroll">
          {/* STEP 1 — pick player */}
          {step === 1 && (
            <>
              <div className="text-sm text-slate-700 mb-3">
                {direction === "outgoing"
                  ? "Which of your clients does this mandate cover?"
                  : "Which mandated player does this mandate concern? Only Tier 3 players are eligible — add the player first via Clients → New player → Mandated."}
              </div>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by player, club, position…"
                className="pm-input mb-3"
                data-testid="wizard-client-search"
              />
              {clients.length === 0 ? (
                <div className="p-6 rounded-xl border-2 border-dashed border-slate-200 text-center text-sm text-slate-500" data-testid="wizard-no-eligible">
                  {direction === "outgoing"
                    ? "No Tier 1 clients in this workspace yet."
                    : "No Tier 3 mandated players yet. Go to Clients → New player → Mandated to add one first."}
                </div>
              ) : (
                <div className="space-y-1.5 max-h-72 overflow-y-auto pm-scroll">
                  {filteredClients.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setPickedClient(c)}
                      data-testid={`wizard-pick-client-${c.id}`}
                      className={`w-full p-2.5 rounded-lg border-2 text-left flex items-center gap-3 transition-all ${
                        pickedClient?.id === c.id ? "border-emerald-500 bg-emerald-50/40" : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="w-9 h-9 rounded-lg bg-slate-200 text-slate-700 flex items-center justify-center font-bold text-xs">
                        {c.full_name.split(/\s+/).map((p) => p[0]).slice(0, 2).join("")}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm truncate">{c.full_name}</div>
                        <div className="text-[11px] text-slate-500 truncate">{c.position} · {c.current_club}</div>
                      </div>
                      {pickedClient?.id === c.id && <Check size={14} className="text-emerald-700 shrink-0" />}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* STEP 2 — partner */}
          {step === 2 && (
            <div className="space-y-3" data-testid="wizard-partner">
              <Field label="Partner agent name *" testid="partner-agent-name">
                <input value={form.partner_agent_name} onChange={(e) => update({ partner_agent_name: e.target.value })} className="pm-input" placeholder="e.g. Roberto Silva" />
              </Field>
              <Field label="Partner agency name" testid="partner-agency-name">
                <input value={form.partner_agency_name} onChange={(e) => update({ partner_agency_name: e.target.value })} className="pm-input" placeholder="e.g. Silva Sports" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Partner email" testid="partner-email">
                  <input type="email" value={form.partner_agent_email} onChange={(e) => update({ partner_agent_email: e.target.value })} className="pm-input" placeholder="roberto@silvasports.it" />
                </Field>
                <Field label="Partner phone" testid="partner-phone">
                  <input value={form.partner_agent_phone} onChange={(e) => update({ partner_agent_phone: e.target.value })} className="pm-input" placeholder="+39 ..." />
                </Field>
              </div>
            </div>
          )}

          {/* STEP 3 — scope */}
          {step === 3 && (
            <div className="space-y-3" data-testid="wizard-scope">
              <div className="grid grid-cols-2 gap-2">
                {SCOPE_TYPES.map((s) => {
                  const active = form.scope_type === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => update({ scope_type: s.id })}
                      data-testid={`wizard-scope-${s.id}`}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${active ? "border-emerald-500 bg-emerald-50/40" : "border-slate-200 hover:border-slate-300"}`}
                    >
                      <div className="font-bold text-sm">{s.label}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">{s.blurb}</div>
                    </button>
                  );
                })}
              </div>
              {form.scope_type === "clubs" && (
                <Field label="Clubs (comma-separated) *" testid="scope-clubs">
                  <input
                    value={form.scope_clubs.join(", ")}
                    onChange={(e) => update({ scope_clubs: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
                    className="pm-input"
                    placeholder="Ajax, PSV, Feyenoord"
                  />
                </Field>
              )}
              {form.scope_type === "leagues" && (
                <Field label="Leagues / countries (comma-separated) *" testid="scope-leagues">
                  <input
                    value={form.scope_leagues.join(", ")}
                    onChange={(e) => update({ scope_leagues: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
                    className="pm-input"
                    placeholder="Serie A, Eredivisie"
                  />
                </Field>
              )}
              <Field label={form.scope_type === "custom" ? "Scope description *" : "Scope description (optional)"} testid="scope-description">
                <textarea
                  value={form.scope_description}
                  onChange={(e) => update({ scope_description: e.target.value })}
                  rows={3}
                  className="pm-input"
                  placeholder='e.g. "Permission to approach Serie A clubs only — Jul to Aug window"'
                />
              </Field>
            </div>
          )}

          {/* STEP 4 — dates + commission + notes */}
          {step === 4 && (
            <div className="space-y-3" data-testid="wizard-dates">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Start date *" testid="start-date">
                  <input type="date" value={form.start_date} onChange={(e) => update({ start_date: e.target.value })} className="pm-input" />
                </Field>
                <Field label="Expiry date *" testid="expiry-date">
                  <input type="date" value={form.expiry_date} onChange={(e) => update({ expiry_date: e.target.value })} className="pm-input" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Remind me … days before" testid="reminder-days">
                  <input type="number" min={1} max={120} value={form.reminder_days_before} onChange={(e) => update({ reminder_days_before: e.target.value })} className="pm-input" />
                </Field>
                <Field label="Commission split % (your share)" testid="commission">
                  <input type="number" min={0} max={100} step="0.1" value={form.commission_split_pct} onChange={(e) => update({ commission_split_pct: e.target.value })} className="pm-input" placeholder="e.g. 30" />
                </Field>
              </div>
              <Field label="Internal notes" testid="notes">
                <textarea value={form.notes} onChange={(e) => update({ notes: e.target.value })} rows={3} className="pm-input" placeholder="Anything to remember about this mandate — never shown in reports." />
              </Field>
            </div>
          )}

          {/* STEP 5 — confirm + optional upload */}
          {step === 5 && (
            <div className="space-y-3" data-testid="wizard-confirm">
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-[12px] space-y-1">
                <Row k="Direction"   v={direction} />
                <Row k="Player"      v={pickedClient?.full_name} />
                <Row k="Partner"     v={`${form.partner_agent_name}${form.partner_agency_name ? " · " + form.partner_agency_name : ""}`} />
                <Row k="Scope"       v={`${form.scope_type}${form.scope_clubs.length ? " · " + form.scope_clubs.join(", ") : ""}${form.scope_leagues.length ? " · " + form.scope_leagues.join(", ") : ""}`} />
                <Row k="Validity"    v={`${form.start_date} → ${form.expiry_date}`} />
                {form.commission_split_pct !== "" && <Row k="Commission" v={`${form.commission_split_pct}%`} />}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-1.5">Mandate document (optional, recommended)</div>
                <label className="block w-full p-4 rounded-xl border-2 border-dashed border-slate-300 hover:border-emerald-400 transition-colors cursor-pointer text-center" data-testid="wizard-doc-dropzone">
                  <input
                    type="file"
                    accept=".pdf,application/pdf,image/png,image/jpeg,image/jpg"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                  {file ? (
                    <div className="text-sm text-slate-700 flex items-center gap-2 justify-center">
                      <Check size={13} className="text-emerald-600" />
                      <b>{file.name}</b>
                      <span className="text-slate-400">· {(file.size / 1024).toFixed(0)} KB</span>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500 flex items-center gap-2 justify-center">
                      <Upload size={13} /> Drop a signed PDF or image here (max 8MB)
                    </div>
                  )}
                </label>
                {!file && (
                  <div className="mt-2 text-[11px] text-amber-700 flex items-start gap-1.5">
                    <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                    No document attached — Playmaker will flag this mandate for compliance follow-up.
                  </div>
                )}
              </div>
              {error && <div className="text-[12px] text-rose-700 flex items-center gap-1.5"><AlertTriangle size={11} /> {error}</div>}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between shrink-0 bg-slate-50/50">
          <button
            onClick={() => (step === 1 ? onClose() : setStep(step - 1))}
            className="pm-btn-secondary h-10 px-3 rounded-xl text-sm font-bold flex items-center gap-1.5"
            data-testid="wizard-back-btn"
            disabled={creating}
          >
            <ChevronLeft size={13} /> {step === 1 ? "Cancel" : "Back"}
          </button>
          {step < 5 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canAdvance()}
              data-testid="wizard-next-btn"
              className="pm-btn-primary h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-1.5 disabled:opacity-50"
            >
              Continue <ChevronRight size={13} />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={creating}
              data-testid="wizard-submit-btn"
              className="pm-btn-primary h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-1.5 disabled:opacity-50"
            >
              {creating ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              Create mandate
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <label className="block" data-testid={`wizard-field-${testid}`}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex">
      <div className="w-24 text-slate-500 capitalize">{k}</div>
      <div className="flex-1 font-bold text-slate-800">{v || "—"}</div>
    </div>
  );
}
